import { basicAuth, customAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import logo from "./logo.svg";
import { chainguardOidcSteps } from "./oidc";

const KEYWORDS = "cgr wolfi apko container image registry sbom cve advisory";

export const chainguard = defineProduct({
  name: "Chainguard",
  tagLine: "The trusted source for open source",
  logo,
  tags: ["engineering"],
  variants: [
    productVariant({
      identifier: "chainguard",
      authLabel: "Registry pull token",
      keywords: KEYWORDS,
      protocol: "http",
      auth: (app) =>
        basicAuth({
          username: app.config.username,
          password: app.secret.password,
        }),
      steps: [
        {
          title: "Create a Chainguard pull token",
          items: [
            {
              kind: "markdown",
              content:
                "In the [Chainguard Console](https://console.chainguard.dev/), open your organization’s settings and create a pull token, or run `chainctl auth configure-docker --pull-token`. Both produce a username and password for `cgr.dev`.",
            },
          ],
        },
        {
          title: "Enter your Chainguard details",
          items: [
            {
              kind: "input",
              label: "Organization name",
              required: true,
              placeholder: "company-name",
              helperText: "Your images live under cgr.dev/<organization name>.",
              target: { kind: "configuration", field: "orgName" },
            },
            {
              kind: "input",
              label: "Pull token username",
              required: true,
              target: { kind: "configuration", field: "username" },
            },
            {
              kind: "input",
              label: "Pull token password",
              required: true,
              target: { kind: "secrets", field: "password" },
            },
          ],
        },
      ],
      defaultUrlPattern: () => "https://cgr\\.dev/.*",
      usageHint: ({ orgName }) =>
        `Images are pulled from cgr.dev/${orgName}/<image>. The pull token is sent as basic auth, the same credentials docker login uses.`,
      connectionTest: {
        protocol: "http",
        request: () => "https://cgr.dev/token?service=cgr.dev",
      },
    }),
    productVariant({
      identifier: "chainguardPlatform",
      authLabel: "OIDC (assumable identity)",
      keywords: KEYWORDS,
      protocol: "http",
      defaultUrlPattern: () =>
        "https://(?:console-api|issuer)\\.enforce\\.dev/.*",
      auth: (app) =>
        customAuth("chainguardFederation", {
          issuer: app.issuer ?? "",
          identityUid: app.config.identityUid,
        }),
      steps: [...chainguardOidcSteps],
      connectionTest: {
        protocol: "http",
        request: () => "https://issuer.enforce.dev/auth/v1/ok",
      },
      // The identity's UIDP is rooted at the organization UID, which nearly
      // every list call needs as `uidp.descendants_of`.
      publicEnvVars: ({ identityUid }): Record<string, string> => {
        const orgId = identityUid?.split("/")[0]?.trim() ?? "";
        return orgId ? { CHAINGUARD_ORG_ID: orgId } : {};
      },
      usageHint: () =>
        "Calls to console-api.enforce.dev are authenticated automatically — send plain HTTPS requests with no Authorization header. CHAINGUARD_ORG_ID holds the organization UID that list endpoints take as `uidp.descendants_of`. Most resources live under /registry/v2/, /iam/v2/, and /vulnerabilities/v2/, paginated with page_size and page_token; SBOMs, vulnerability reports, and end-of-life tags have no v2 equivalent and stay on /registry/v1/. GET https://issuer.enforce.dev/auth/v1/ok reports the identity and its effective roles.",
    }),
  ],
});
